import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import {
  LuArrowRight,
  LuCircleCheckBig,
  LuMessageSquareWarning,
  LuPencilLine,
} from "react-icons/lu";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import { readableError } from "@/lib/errors";
import { CardGridSkeleton } from "../components/LoadingSkeletons";
import { toastError, toastSuccess } from "../components/ui/toaster";
import {
  ReviewFormFields,
  missingRequiredReviewFields,
  type ReviewFieldValue,
} from "@/components/ReviewFormFields";
import type { Id } from "@convex/_generated/dataModel";
import { useEffectivePersonId } from "../impersonation";
import { formatDay } from "./ActivityAdminShared";
import { LuShieldUser } from "../components/icons";

type PendingReview =
  FunctionReturnType<typeof api.reviews.listPendingForViewer>[number];

/**
 * What is waiting for this person's signature.
 *
 * The queue already tells us WHICH reviewer slot the viewer may sign for -- a
 * task can name several, and somebody can be two of them at once -- so the
 * buttons are per slot rather than per task.
 */
/**
 * The composer's values into what the mutation stores.
 *
 * Unanswered optional fields are dropped rather than stored as null: a key
 * that is absent reads as "not answered", where a null would have to be told
 * apart from a deliberate blank.
 */
function toResponseData(values: Record<string, ReviewFieldValue>) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) =>
      value !== null && value !== undefined && String(value) !== ""),
  ) as Record<string, string | number | boolean>;
}

/**
 * The approve / request-revision form for one reviewer slot.
 *
 * Its own component because all of its state is its own: which way the
 * decision is going, the comment, and the answers to the review form. While
 * that state sat on the page, typing a single character of a comment
 * re-rendered every other row in the queue -- rows that cannot change while
 * somebody writes.
 */
function DecisionComposer({
  review,
  slot,
  onClose,
}: {
  review: PendingReview;
  slot: PendingReview["actionable"][number];
  onClose: () => void;
}) {
  const approveTask = useMutation(api.reviews.approveTask);
  const requestTaskRevision = useMutation(api.reviews.requestTaskRevision);
  const effectivePersonId = useEffectivePersonId() as Id<"persons"> | null;

  const [decision, setDecision] = useState<"approve" | "revise">("approve");
  const [comment, setComment] = useState("");
  const [reviewValues, setReviewValues] = useState<Record<string, ReviewFieldValue>>({});
  const [busy, setBusy] = useState(false);

  const reviewFields = review.reviewFormSchema?.fields ?? [];

  async function submit() {
    // A revision request without a reason is useless to whoever has to act on
    // it; an approval needs no words.
    if (decision === "revise" && comment.trim() === "") {
      toastError("Say what needs changing.");
      return;
    }

    // Only an approval carries the assessment, so only an approval has to
    // have answered it.
    if (decision === "approve" && reviewFields.length > 0) {
      const missing = missingRequiredReviewFields(reviewFields, reviewValues);
      if (missing.length > 0) {
        toastError("Still to answer", missing.join(", "));
        return;
      }
    }

    setBusy(true);
    try {
      const args = { taskId: review.taskId, reviewerSpec: slot.spec, comment: comment.trim(), effectivePersonId };
      if (decision === "approve") {
        await approveTask(
          reviewFields.length > 0
            ? { ...args, responseData: toResponseData(reviewValues) }
            : args,
        );
        toastSuccess("Approved", review.taskTitle);
      } else {
        await requestTaskRevision(args);
        toastSuccess("Revision requested", review.taskTitle);
      }
      onClose();
    } catch (error) {
      // The sentence first; the browser's own words second, where they
      // add anything. On their own they tell the reader nothing.
      const readable = readableError(error, "The decision could not be recorded");
      toastError(readable.message, readable.detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap={3}>
      <HStack gap={2}>
        <Button
          size="xs"
          variant={decision === "approve" ? "solid" : "outline"}
          colorPalette="green"
          onClick={() => setDecision("approve")}
        >
          Approve
        </Button>
        <Button
          size="xs"
          variant={decision === "revise" ? "solid" : "outline"}
          colorPalette="orange"
          onClick={() => setDecision("revise")}
        >
          Request revision
        </Button>
      </HStack>

      {decision === "approve" && reviewFields.length > 0 ? (
        <Box borderWidth="1px" borderColor="border" rounded="md" bg="bg.muted" p={4}>
          <ReviewFormFields
            fields={reviewFields}
            values={reviewValues}
            disabled={busy}
            onChange={(key, value) => setReviewValues((current) => ({ ...current, [key]: value }))}
          />
        </Box>
      ) : null}

      <Textarea
        rows={3}
        value={comment}
        placeholder={decision === "approve" ? "Optional comment" : "What needs changing?"}
        onChange={(event) => setComment(event.target.value)}
      />

      <HStack gap={3}>
        <Button
          colorPalette={decision === "approve" ? "green" : "orange"}
          loading={busy}
          onClick={() => void submit()}
        >
          {decision === "approve" ? "Approve" : "Request revision"}
        </Button>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </HStack>
    </Stack>
  );
}

/** In which role, as the words after "as": a named reviewer's label is their name. */
function asRole(slot: PendingReview["actionable"][number]): string {
  return slot.spec.kind === "person" ? slot.label : slot.label.toLowerCase();
}

/**
 * One piece of work in the queue.
 *
 * The role it is yours in is said once, above what you can do -- "Your
 * decision as supervisor", with the shield the app uses for "because of a
 * role" -- as on the activity page, rather than in a badge beside the title
 * and again in the form.
 */
function ReviewCard({
  review,
  openKey,
  setOpenKey,
}: {
  review: PendingReview;
  openKey: string | null;
  setOpenKey: (key: string | null) => void;
}) {
  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={3} p={5}>
        <Flex align="flex-start" justify="space-between" gap={3}>
          <Stack gap={1} minW={0}>
            <Heading size="md">{review.taskTitle}</Heading>
            <Text fontSize="sm" color="fg.muted">
              {review.personName} — {review.activityTitle}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {/* The step's own deadline where it has one, since that is the
                  date that binds and the one the queue is ordered by. The
                  activity's is named beside it, so the shorter deadline does
                  not look like the whole story. */}
              {review.taskDueDay
                ? `Step due ${formatDay(review.taskDueDay)}${review.dueDay ? ` · activity ${formatDay(review.dueDay)}` : ""}`
                : `Due ${formatDay(review.dueDay)}`}
              {review.kind === "approve" ? ` · ${review.reviewSummaryLabel}` : ""}
            </Text>
          </Stack>
          <Button asChild variant="ghost" size="sm" flexShrink={0}>
            <Link to="/activities/$personActivityId" params={{ personActivityId: String(review.personActivityId) }} search={{ from: "reviews" }}>
              Open
              <LuArrowRight />
            </Link>
          </Button>
        </Flex>

        {review.kind === "complete" ? (
          <Stack gap={2} borderTopWidth="1px" borderColor="border" pt={3}>
            <RoleLabel>
              Yours to fill in{review.actorRole === null ? "" : ` as ${review.actorRole}`}
            </RoleLabel>
            <Button asChild size="sm" colorPalette="blue" alignSelf="flex-start">
              <Link to="/activities/$personActivityId" params={{ personActivityId: String(review.personActivityId) }} search={{ from: "reviews" }}>
                <LuPencilLine />
                Fill it in
              </Link>
            </Button>
          </Stack>
        ) : review.actionable.map((slot, slotIndex) => {
          const key = `${review.taskId}:${slotIndex}`;
          return (
            <Stack key={key} gap={2} borderTopWidth="1px" borderColor="border" pt={3}>
              <RoleLabel>Your decision as {asRole(slot)}</RoleLabel>
              {openKey === key ? (
                <DecisionComposer review={review} slot={slot} onClose={() => setOpenKey(null)} />
              ) : (
                <HStack gap={3}>
                  <Button size="sm" colorPalette="green" onClick={() => setOpenKey(key)}>
                    <LuCircleCheckBig />
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" colorPalette="orange" onClick={() => setOpenKey(key)}>
                    <LuMessageSquareWarning />
                    Request revision
                  </Button>
                </HStack>
              )}
            </Stack>
          );
        })}
      </Card.Body>
    </Card.Root>
  );
}

/** The step's "Your decision as ..." label, with the role shield. */
function RoleLabel({ children }: { children: ReactNode }) {
  return (
    <Text fontSize="sm" fontWeight="medium" display="inline-flex" alignItems="center" gap={1.5}>
      <Icon as={LuShieldUser} boxSize={4} color="fg.muted" />
      {children}
    </Text>
  );
}

/**
 * The "My reviews" tab of Events & Activities: the steps on other people's
 * activities that wait on this person, to sign off or to fill in.
 *
 * A tab beside My activities rather than a page of its own, because it is the
 * same kind of list -- what is mine to do -- and one place for that is easier
 * to find than two.
 */
export function MyReviews() {
  // Follows impersonation, like the rest of the reads: looking at the app as
  // a supervisor should show that supervisor's queue.
  const effectivePersonId = useEffectivePersonId();
  const pending = useQuery(api.reviews.listPendingForViewer, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });

  // One open composer at a time, keyed by task + slot: a comment belongs to
  // the decision being made, not to the page.
  const [openKey, setOpenKey] = useState<string | null>(null);

  return (
    <Stack gap={5} pb={{ base: 6, lg: 8 }}>

      {pending === undefined ? (
        <CardGridSkeleton label="Loading reviews" cards={4} columns={{ base: 1, lg: 2 }} />
      ) : pending.length === 0 ? (
        <Card.Root borderWidth="1px" borderStyle="dashed" borderColor="border">
          <Card.Body gap={2} p={8}>
            <Heading size="md">Nothing waiting for you</Heading>
            <Text fontSize="sm" color="fg.muted">
              Work appears here when someone submits something you sign off.
            </Text>
          </Card.Body>
        </Card.Root>
      ) : (
        // Two kinds of work, side by side on wide screens: what somebody
        // submitted for you to sign, and the steps you fill in yourself.
        // The column says which, so the cards need no badge for it.
        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6} alignItems="start">
          {([
            { kind: "approve", title: "To sign off", icon: LuCircleCheckBig },
            { kind: "complete", title: "To fill in", icon: LuPencilLine },
          ] as const).map((column) => {
            const items = pending.filter((review) => review.kind === column.kind);
            return (
              <Stack as="section" key={column.kind} gap={3}>
                <HStack gap={2} fontSize="sm" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="blue.solid">
                  <Icon as={column.icon} boxSize={4} />
                  {column.title}
                  <Text as="span" color="fg.muted">· {items.length}</Text>
                </HStack>
                {items.length === 0 ? (
                  <Text fontSize="sm" color="fg.subtle">Nothing here.</Text>
                ) : items.map((review) => (
                  <ReviewCard key={review.taskId} review={review} openKey={openKey} setOpenKey={setOpenKey} />
                ))}
              </Stack>
            );
          })}
        </SimpleGrid>
      )}
    </Stack>
  );
}
